// src/constants/pageSEO.js

import constants from "./constants";
import globalState from "../utils/globalState";

let cachedUserParams = null;

const getAllUserParams = () => {
  if (!cachedUserParams) {
    cachedUserParams = globalState.getAllUserParams();
  }
  return cachedUserParams;
};

const getCurrentAgentName = () => getAllUserParams().agentName || constants.AGENT_NAME;
const getCurrentAgentTitle = () => getAllUserParams().agentTitle || constants.AGENT_TITLE;
const getCurrentAgentNumber = () => getAllUserParams().agentNumber || constants.AGENT_NUMBER;
const getCurrentAgentEmail = () => getAllUserParams().agentEmail || constants.AGENT_EMAIL;
const getCurrentBrokerage = () => getAllUserParams().brokerage || constants.BROKERAGE;
const getCurrentQuote = () => getAllUserParams().quote || constants.QUOTE;
const getCurrentAbout = () => getAllUserParams().about || constants.ABOUT_DESCRIPTION;
const getCurrentLogo = () => getAllUserParams().logo || constants.LOGO;
const getCurrentHeadshot = () => getAllUserParams().headshot || constants.HEADSHOT;

const pageSEO = {
  home: () => ({
    title: `${getCurrentAgentName()} - ${getCurrentAgentTitle()} at ${getCurrentBrokerage()}`,
    description: `${getCurrentAbout()} ${getCurrentQuote()}`,
    keywords: `${getCurrentAgentName()}, ${getCurrentBrokerage()}, real estate, ${getCurrentAgentTitle()}, property expert`,
    path: "/",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: getCurrentAgentName(),
      image: getCurrentHeadshot(),
      description: getCurrentAbout(),
      telephone: getCurrentAgentNumber(),
      email: getCurrentAgentEmail(),
      worksFor: {
        "@type": "RealEstateOrganization",
        name: getCurrentBrokerage(),
        logo: getCurrentLogo(),
      },
    },
  }),

  listings: () => ({
    title: `Property Listings | ${getCurrentAgentName()}`,
    description: `Browse curated properties with ${getCurrentAgentName()} at ${getCurrentBrokerage()}. Find your dream home with expert guidance.`,
    keywords: `property listings, real estate, ${getCurrentAgentName()}, ${getCurrentBrokerage()}, homes for sale`,
    path: "/listings",
  }),

  listing: (listing) => ({
    // title: `${listing.address} - ${listing.location} | ${getCurrentAgentName()}`,
    title: `${listing.StreetNumber} ${listing.StreetName} ${listing.StreetSuffix} - ${listing.City} ${listing.StateOrProvince} | ${getCurrentAgentName()}`,
    description: `${listing.bedrooms} bedroom ${listing.bathrooms} bathroom ${listing.propertyType} located in ${listing.location}, priced at ${listing.price}. Contact ${getCurrentAgentName()} for more information.`,
    keywords: `${listing.location}, ${listing.propertyType}, ${listing.bedrooms} bedroom, ${listing.bathrooms} bathroom, property, ${getCurrentAgentName()}`,
    path: `/listing/${listing.id}`,
    image: listing.mainImage,
    type: "product",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: listing.address,
      description: listing.description,
      image: listing.mainImage,
      offers: {
        "@type": "Offer",
        priceCurrency: "CAD",
        price: listing?.price?.replace(/[^\d.]/g, ""),
        availability: "https://schema.org/InStock",
      },
      seller: {
        "@type": "RealEstateAgent",
        name: getCurrentAgentName(),
        telephone: getCurrentAgentNumber(),
        email: getCurrentAgentEmail(),
        worksFor: {
          "@type": "RealEstateOrganization",
          name: getCurrentBrokerage(),
        },
      },
    },
  }),

  about: () => ({
    title: `About ${getCurrentAgentName()} | ${getCurrentBrokerage()}`,
    description: `Learn more about ${getCurrentAgentName()}, ${getCurrentAgentTitle()} at ${getCurrentBrokerage()}. ${getCurrentAbout()}`,
    keywords: `about ${getCurrentAgentName()}, ${getCurrentBrokerage()}, real estate agent, ${getCurrentAgentTitle()}`,
    path: "/about",
  }),

  location: (location) => ({
    title: `${location.name} Real Estate | ${getCurrentAgentName()} at ${getCurrentBrokerage()}`,
    description: `Browse properties and market trends in ${location.name} with ${getCurrentAgentName()} from ${getCurrentBrokerage()}.`,
    keywords: `${location.name}, real estate, properties, ${getCurrentAgentName()}, ${getCurrentBrokerage()}`,
    path: `/location/${location.slug}`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Place",
      name: location.name,
      description: `Real estate market insights and property listings in ${location.name}.`,
    },
  }),

  agents: () => ({
    title: `Our Real Estate Agents | ${getCurrentBrokerage()}`,
    description: `Meet the professional agents partnering with ${getCurrentAgentName()} at ${getCurrentBrokerage()}.`,
    keywords: `real estate agents, ${getCurrentBrokerage()}, realtor team`,
    path: "/agents",
  }),

  agent: (agent) => ({
    title: `${agent.name} - ${agent.title}`,
    description: `Meet ${agent.name}, our professional ${agent.title}. ${
      agent.bio ? agent.bio.substring(0, 150) + "..." : ""
    }`,
    keywords: `${agent.name}, real estate agent, realtor, property expert`,
    path: `/agents/${agent.name.toLowerCase().replace(/\s+/g, "-")}`,
    image: agent.img,
    type: "profile",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: agent.name,
      image: agent.img,
      description: agent.bio,
      telephone: agent.number,
      email: agent.email,
      worksFor: {
        "@type": "RealEstateOrganization",
        name: getCurrentBrokerage(),
      },
    },
  }),

  contact: () => ({
    title: `Contact ${getCurrentAgentName()} | ${getCurrentBrokerage()}`,
    description: `Contact ${getCurrentAgentName()}, ${getCurrentAgentTitle()} at ${getCurrentBrokerage()} for professional real estate advice. Phone: ${getCurrentAgentNumber()}, Email: ${getCurrentAgentEmail()}`,
    keywords: `contact ${getCurrentAgentName()}, real estate agent, ${getCurrentBrokerage()}, property consultation`,
    path: "/contact",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPoint",
      telephone: getCurrentAgentNumber(),
      contactType: "customer service",
      email: getCurrentAgentEmail(),
      areaServed: "CA",
      availableLanguage: ["English"],
    },
  }),

  evaluation: () => ({
    title: `Free Property Evaluation | ${getCurrentAgentName()}`,
    description: `Get a free market evaluation from ${getCurrentAgentName()} at ${getCurrentBrokerage()}. Understand your home's value today.`,
    keywords: `property evaluation, home value, market analysis, ${getCurrentAgentName()}`,
    path: "/evaluation",
  }),

  calculator: () => ({
    title: `Mortgage Calculator | ${getCurrentAgentName()}`,
    description: `Estimate your mortgage payments and affordability. Get expert guidance from ${getCurrentAgentName()} at ${getCurrentBrokerage()}.`,
    keywords: `mortgage calculator, loan calculator, ${getCurrentAgentName()}, ${getCurrentBrokerage()}`,
    path: "/calculator",
  }),

  reports: () => ({
    title: `Real Estate Market Reports | ${getCurrentAgentName()}`,
    description: `Stay informed with real estate market reports curated by ${getCurrentAgentName()} at ${getCurrentBrokerage()}.`,
    keywords: `real estate market, market reports, ${getCurrentAgentName()}, ${getCurrentBrokerage()}`,
    path: "/real-estate-reports",
  }),

  commercial: () => ({
    title: `Commercial Real Estate | ${getCurrentBrokerage()}`,
    description: `Explore commercial listings and investment opportunities with ${getCurrentAgentName()} at ${getCurrentBrokerage()}.`,
    keywords: `commercial real estate, business properties, ${getCurrentAgentName()}, ${getCurrentBrokerage()}`,
    path: "/commercial",
  }),

  sellerGuide: () => ({
    title: `Seller's Guide | ${getCurrentAgentName()}`,
    description: `Professional guide to selling your home with ${getCurrentAgentName()} at ${getCurrentBrokerage()}.`,
    keywords: `home selling, seller's guide, ${getCurrentAgentName()}, ${getCurrentBrokerage()}`,
    path: "/selling-guide",
  }),

  buyerGuide: () => ({
    title: `Buyer's Guide | ${getCurrentAgentName()}`,
    description: `Learn how to buy a home with insights from ${getCurrentAgentName()} at ${getCurrentBrokerage()}.`,
    keywords: `home buying, buyer's guide, ${getCurrentAgentName()}, ${getCurrentBrokerage()}`,
    path: "/buying-guide",
  }),
};

export default pageSEO;
